import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import deleteSvg from '../assets/details-bucket/delete.svg';
import settingsSvg from '../assets/details-bucket/settings.svg';
import { useAuth } from '../contexts/AuthContext';
import { useApi } from '../contexts/ApiContext';
import { useWithTokenRefresh } from '../utils/api';
import { LinksTypes } from '@flashbacktech/flashbackclient/api';
import { toastError, toastSuccess } from '../utils/toast';

const ActivationLinks: React.FC = () => {
  const { authState } = useAuth();
  const { apiClient } = useApi();
  const withTokenRefresh = useWithTokenRefresh();

  // Check if user has access (email ends with flashback.tech)
  const hasAccess = authState?.user?.email?.endsWith('@flashback.tech') || false;

  // State
  const [links, setLinks] = useState<LinksTypes.ActivationLinkDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [take] = useState(20);
  const [skip, setSkip] = useState(0);

  // Filters
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');
  const [status, setStatus] = useState<'activated' | 'pending' | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

  // Dialog states
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedLink, setSelectedLink] = useState<LinksTypes.ActivationLinkDTO | null>(null);

  // Form states
  const [formDescription, setFormDescription] = useState('');
  const [formEmail, setFormEmail] = useState('');

  // Debounce search query - wait 2.5 seconds after user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 2500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Load links
  const loadLinks = useCallback(async () => {
    if (!hasAccess) return;
    
    setLoading(true);
    try {
      const query: LinksTypes.GetLinksRequest = {
        skip,
        take,
        status: status !== 'all' ? status : undefined,
        from: fromDate || undefined,
        to: toDate || undefined,
        text: debouncedSearchQuery || undefined,
      };

      const response = await withTokenRefresh(() => apiClient.getLinks(query));
      
      if (response.success) {
        setLinks(response.links || []);
        setTotal(response.total || 0);
      } else {
        toastError(response.message || 'Failed to load activation links');
      }
    } catch (error: any) {
      toastError(error?.message || 'Failed to load activation links');
    } finally {
      setLoading(false);
    }
  }, [hasAccess, skip, take, status, fromDate, toDate, debouncedSearchQuery, withTokenRefresh, apiClient]);

  useEffect(() => {
    loadLinks();
  }, [loadLinks]);

  // Handle page change
  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    setSkip((value - 1) * take);
  };

  // Open add dialog
  const handleAddClick = () => {
    setFormDescription('');
    setFormEmail('');
    setAddDialogOpen(true);
  };

  // Open edit dialog
  const handleEditClick = (link: LinksTypes.ActivationLinkDTO) => {
    setSelectedLink(link);
    setFormDescription(link.description);
    setFormEmail(link.email);
    setEditDialogOpen(true);
  };

  // Open delete dialog
  const handleDeleteClick = (link: LinksTypes.ActivationLinkDTO) => {
    setSelectedLink(link);
    setDeleteDialogOpen(true);
  };

  // Handle add
  const handleAdd = async () => {
    if (!formDescription.trim() || !formEmail.trim()) {
      toastError('Please fill in all required fields');
      return;
    }

    try {
      const response = await withTokenRefresh(() =>
        apiClient.createLink({
          name: formDescription,
          email: formEmail,
        })
      );

      if (response.success) {
        toastSuccess('The link has been created. Please, copy the link bellow to send it the mail address. (copy button)', 10000);
        setAddDialogOpen(false);
        loadLinks();
      } else {
        toastError('Failed to create activation link');
      }
    } catch (error: any) {
      toastError(error?.message || 'Failed to create activation link');
    }
  };

  // Handle edit
  const handleEdit = async () => {
    if (!selectedLink) return;

    const updateData: LinksTypes.UpdateLinkRequest = {};
    
    if (formDescription.trim() !== selectedLink.description) {
      updateData.description = formDescription.trim();
    }

    // Only allow email edit if link is not activated
    if (!selectedLink.activatedAt && formEmail.trim() !== selectedLink.email) {
      updateData.email = formEmail.trim();
    }

    if (Object.keys(updateData).length === 0) {
      toastError('No changes to save');
      return;
    }

    try {
      const response = await withTokenRefresh(() =>
        apiClient.updateLink(selectedLink.id, updateData)
      );

      if (response.success) {
        toastSuccess('Activation link updated successfully');
        setEditDialogOpen(false);
        setSelectedLink(null);
        loadLinks();
      } else {
        toastError(response.message || 'Failed to update activation link');
      }
    } catch (error: any) {
      toastError(error?.message || 'Failed to update activation link');
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!selectedLink) return;

    try {
      const response = await withTokenRefresh(() =>
        apiClient.deleteLink(selectedLink.id)
      );

      if (response.success) {
        toastSuccess('Activation link deleted successfully');
        setDeleteDialogOpen(false);
        setSelectedLink(null);
        loadLinks();
      } else {
        toastError(response.message || 'Failed to delete activation link');
      }
    } catch (error: any) {
      toastError(error?.message || 'Failed to delete activation link');
    }
  };

  // Handle copy activation link
  const handleCopyLink = async (link: LinksTypes.ActivationLinkDTO) => {
    if (!link.activationLink) {
      toastError('Activation link not available');
      return;
    }

    try {
      await navigator.clipboard.writeText(link.activationLink);
      toastSuccess('Activation link copied to clipboard');
    } catch (error: any) {
      toastError('Failed to copy activation link to clipboard');
    }
  };

  // Format date
  const formatDate = (date: string | Date | null) => {
    if (!date) return '-';
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  if (!hasAccess) {
    return (
      <div 
        className="min-h-screen px-4 sm:px-6 lg:px-8 flex items-center justify-center"
        style={{
          background: 'linear-gradient(135deg, rgba(120, 140, 255, 0.02) 0%, rgba(255, 255, 255, 1) 50%, rgba(235, 189, 163, 0.02) 100%)'
        }}
      >
        <div className="text-center">
          <Typography variant="h5" style={{ fontWeight: 600, color: '#181D27', marginBottom: '8px' }}>
            Access Denied
          </Typography>
          <Typography variant="body1" style={{ color: '#717680' }}>
            This page is only available for Flashback team members.
          </Typography>
        </div>
      </div>
    );
  }

  return (
    <div
      className="w-full px-2 sm:px-4"
      style={{
        background: 'transparent',
        maxWidth: '100%',
        overflow: 'hidden',
      }}
    >
      {/* Header with title and add button */}
      <div className="w-full" style={{ margin: '0 auto', maxWidth: '100%' }}>
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
          <Typography
            style={{
              fontWeight: 600,
              fontSize: '20px',
              lineHeight: '30px',
            }}
          >
            Activation Links
          </Typography>
          <button
            onClick={handleAddClick}
            className="px-4 h-10 flex items-center gap-2 text-white rounded-lg transition-all hover:shadow-md"
            style={{
              backgroundColor: '#617BFF',
              fontSize: '14px',
              fontWeight: 500,
            }}
          >
            <AddIcon style={{ fontSize: '18px' }} />
            Add Link
          </button>
        </div>

        {/* Search and Filters Bar */}
        <div className="mb-4 p-3 sm:p-4 bg-white rounded-xl border border-gray-200">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {/* Date From */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">From Date</label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Date To */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">To Date</label>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as 'activated' | 'pending' | 'all')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All</option>
                <option value="pending">Pending</option>
                <option value="activated">Activated</option>
              </select>
            </div>

            {/* Text Search */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
              <div className="relative">
                <SearchIcon
                  style={{
                    position: 'absolute',
                    left: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#9CA3AF',
                    fontSize: '18px',
                  }}
                />
                <input
                  type="text"
                  placeholder="Email, description, creator..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  style={{
                    fontSize: '14px',
                  }}
                />
              </div>
            </div>
          </div>

          {/* Clear Filters Button */}
          <div className="mt-4 flex justify-end">
            <button
              onClick={() => {
                setFromDate('');
                setToDate('');
                setStatus('all');
                setSearchQuery('');
              }}
              className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Clear Filters
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-gray-200" style={{ overflow: 'hidden', maxWidth: '100%' }}>
          {loading ? (
            <div className="flex justify-center items-center p-12">
              <div
                className="animate-spin rounded-full h-12 w-12 border-b-2"
                style={{ borderColor: '#617BFF' }}
              />
            </div>
          ) : (
            <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
              <table className="w-full" style={{ minWidth: '500px' }}>
                <thead>
                  <tr
                    className="border-b border-gray-200"
                    style={{ backgroundColor: 'white', height: '40px' }}
                  >
                    <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="hidden sm:table-cell px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="hidden md:table-cell px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-3 sm:px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {links.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-12 text-center text-gray-500">
                        No activation links found
                      </td>
                    </tr>
                  ) : (
                    links.map((link) => (
                      <tr
                        key={link.id}
                        className="hover:bg-gray-50 transition-all"
                        style={{ height: '56px' }}
                      >
                        <td className="px-3 sm:px-4 py-3">
                          <Typography variant="body1" style={{ color: '#181D27', fontSize: '14px', maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {link.description}
                          </Typography>
                        </td>
                        <td className="px-3 sm:px-4 py-3">
                          <Typography variant="body1" style={{ color: '#717680', fontSize: '14px', maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {link.email}
                          </Typography>
                        </td>
                        <td className="hidden sm:table-cell px-3 sm:px-4 py-3 whitespace-nowrap">
                          <Typography variant="body1" style={{ color: '#717680', fontSize: '14px' }}>
                            {formatDate(link.createdAt)}
                          </Typography>
                        </td>
                        <td className="hidden md:table-cell px-3 sm:px-4 py-3">
                          <span
                            className="px-2 py-1 text-xs font-medium rounded-full"
                            style={{
                              backgroundColor: link.activatedAt ? '#D1FAE5' : '#FEF3C7',
                              color: link.activatedAt ? '#065F46' : '#92400E',
                            }}
                          >
                            {link.activatedAt ? 'Activated' : 'Pending'}
                          </span>
                        </td>
                        <td className="px-3 sm:px-4 py-3">
                          <div className="flex gap-1 justify-end">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCopyLink(link);
                              }}
                              className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                              title="Copy activation link"
                            >
                              <ContentCopyIcon style={{ fontSize: '18px' }} />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditClick(link);
                              }}
                              className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                              title="Edit"
                            >
                              <img src={settingsSvg} alt="Edit" style={{ width: '18px', height: '18px' }} />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteClick(link);
                              }}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Delete"
                            >
                              <img src={deleteSvg} alt="Delete" style={{ width: '18px', height: '18px' }} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {total > take && (
          <div className="mt-6 flex justify-center">
            <div className="flex gap-2">
              <button
                onClick={() => handlePageChange(null as any, Math.max(1, page - 1))}
                disabled={page === 1}
                className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Previous
              </button>
              <span className="px-4 py-2 text-sm text-gray-700">
                Page {page} of {Math.ceil(total / take)}
              </span>
              <button
                onClick={() => handlePageChange(null as any, Math.min(Math.ceil(total / take), page + 1))}
                disabled={page >= Math.ceil(total / take)}
                className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Add Dialog */}
      <Dialog 
        open={addDialogOpen} 
        onClose={() => setAddDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          style: {
            borderRadius: '12px',
            padding: '8px',
          }
        }}
      >
        <DialogTitle style={{ fontWeight: 600, fontSize: '18px', color: '#181D27', paddingBottom: '8px' }}>
          Add Activation Link
        </DialogTitle>
        <DialogContent style={{ paddingTop: '16px' }}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter description"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={formEmail}
                onChange={(e) => setFormEmail(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter email"
                required
              />
            </div>
          </div>
        </DialogContent>
        <DialogActions style={{ padding: '16px 24px' }}>
          <button
            onClick={() => setAddDialogOpen(false)}
            className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleAdd}
            className="px-4 py-2 text-sm text-white rounded-lg transition-all hover:shadow-md"
            style={{ backgroundColor: '#617BFF' }}
          >
            Add Link
          </button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog 
        open={editDialogOpen} 
        onClose={() => setEditDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          style: {
            borderRadius: '12px',
            padding: '8px',
          }
        }}
      >
        <DialogTitle style={{ fontWeight: 600, fontSize: '18px', color: '#181D27', paddingBottom: '8px' }}>
          Edit Activation Link
        </DialogTitle>
        <DialogContent style={{ paddingTop: '16px' }}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter description"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={formEmail}
                onChange={(e) => setFormEmail(e.target.value)}
                disabled={!!selectedLink?.activatedAt}
                className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  selectedLink?.activatedAt ? 'bg-gray-100 cursor-not-allowed' : ''
                }`}
                placeholder="Enter email"
                required
              />
              {selectedLink?.activatedAt && (
                <p className="mt-1 text-xs text-gray-500">
                  Email cannot be edited for activated links
                </p>
              )}
            </div>
          </div>
        </DialogContent>
        <DialogActions style={{ padding: '16px 24px' }}>
          <button
            onClick={() => setEditDialogOpen(false)}
            className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleEdit}
            className="px-4 py-2 text-sm text-white rounded-lg transition-all hover:shadow-md"
            style={{ backgroundColor: '#617BFF' }}
          >
            Save Changes
          </button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog 
        open={deleteDialogOpen} 
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: '12px',
            padding: '8px',
          }
        }}
      >
        <DialogTitle style={{ fontWeight: 600, fontSize: '18px', color: '#181D27', paddingBottom: '8px' }}>
          Delete Activation Link
        </DialogTitle>
        <DialogContent style={{ paddingTop: '16px' }}>
          <Typography style={{ color: '#717680', fontSize: '14px' }}>
            Are you sure you want to delete the activation link for{' '}
            <strong style={{ color: '#181D27' }}>{selectedLink?.email}</strong>?
            <br />
            <br />
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions style={{ padding: '16px 24px' }}>
          <button
            onClick={() => setDeleteDialogOpen(false)}
            className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleDelete}
            className="px-4 py-2 text-sm text-white rounded-lg transition-all hover:shadow-md"
            style={{ backgroundColor: '#DC2626' }}
          >
            Delete
          </button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ActivationLinks;

